import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Slider,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  ViewStream,
  SwipeRight,
  SwipeLeft,
  Star,
  Delete,
  Archive,
  MarkEmailRead,
  Image,
  FormatListBulleted,
} from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MailViewOptions: React.FC = () => {
  const { settings, isLoading, isSaving, updateSettings } = useSettingsStore();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // SettingsLayout already fetches settings

  const handleSettingChange = async (key: string, value: any) => {
    try {
      await updateSettings({ [key]: value });
      setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} color="primary" />
      </Box>
    );
  }

  const swipeActions = [
    { value: 'archive', label: 'Archive', icon: <Archive />, color: 'success.main' },
    { value: 'delete', label: 'Delete', icon: <Delete />, color: 'error.main' },
    { value: 'read', label: 'Mark as Read', icon: <MarkEmailRead />, color: 'primary.main' },
    { value: 'star', label: 'Star', icon: <Star />, color: 'warning.main' },
    { value: 'none', label: 'None', icon: null, color: 'text.secondary' },
  ];

  return (
    <Box>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h5">Mail View Options</Typography>
        {isSaving && <CircularProgress size={16} color="primary" />}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<ViewList />} label="Layout" iconPosition="start" />
        <Tab icon={<FormatListBulleted />} label="List View" iconPosition="start" />
        <Tab icon={<SwipeRight />} label="Gestures" iconPosition="start" />
        <Tab icon={<Image />} label="Content" iconPosition="start" />
      </Tabs>

      {/* Layout Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Reading Pane Position */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Reading Pane
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[
              { value: 'right', label: 'Right', desc: 'Preview on right side', icon: <ViewModule /> },
              { value: 'bottom', label: 'Bottom', desc: 'Preview below list', icon: <ViewStream /> },
              { value: 'off', label: 'Off', desc: 'No preview pane', icon: <ViewList /> },
            ].map((option) => (
              <Box
                key={option.value}
                onClick={() => handleSettingChange('previewPane', option.value)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: (settings.previewPane || 'right') === option.value ? '2px solid' : '1px solid',
                  borderColor: (settings.previewPane || 'right') === option.value ? 'primary.main' : 'divider',
                  bgcolor: (settings.previewPane || 'right') === option.value ? 'primary.light' : 'background.paper',
                  minWidth: 140,
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Box sx={{ color: (settings.previewPane || 'right') === option.value ? 'primary.main' : 'text.secondary', mb: 1 }}>
                  {option.icon}
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: (settings.previewPane || 'right') === option.value ? 'primary.main' : 'text.primary' 
                  }}
                >
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">{option.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Inbox Type */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Inbox Type
          </Typography>
          
          <RadioGroup
            value={settings.inboxType || 'default'}
            onChange={(e) => handleSettingChange('inboxType', e.target.value)}
          >
            <FormControlLabel
              value="default"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Default</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Simple chronological inbox
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="important"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Important first</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Important and starred messages at top
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="unread"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Unread first</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unread messages at top
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="priority"
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Priority Inbox</Typography>
                  <Chip label="AI" size="small" color="warning" sx={{ height: 18 }} />
                </Box>
              }
            />
          </RadioGroup>
        </Paper>

        {/* Threading */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Conversation Threading
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.enableThreading ?? true}
                onChange={(e) => handleSettingChange('enableThreading', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Group emails by conversation</Typography>
                <Typography variant="caption" color="text.secondary">Related emails will be grouped together</Typography>
              </Box>
            }
          />

          {settings.enableThreading && (
            <Box sx={{ ml: 6, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.newestOnTop ?? true}
                    onChange={(e) => handleSettingChange('newestOnTop', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Newest messages on top</Typography>
                    <Typography variant="caption" color="text.secondary">Show latest reply first in conversations</Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* List View Tab */}
      <TabPanel value={tabValue} index={1}>
        {/* Density */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            List Density
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {['comfortable', 'default', 'compact'].map((density) => (
              <Box
                key={density}
                onClick={() => handleSettingChange('listDensity', density)}
                sx={{
                  p: 2,
                  flex: 1,
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: (settings.listDensity || 'default') === density ? '2px solid' : '1px solid',
                  borderColor: (settings.listDensity || 'default') === density ? 'primary.main' : 'divider',
                  bgcolor: (settings.listDensity || 'default') === density ? 'primary.light' : 'background.paper',
                  textAlign: 'center',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: density === 'comfortable' ? 1 : density === 'compact' ? 0.25 : 0.5,
                  mb: 1,
                  mx: 'auto',
                  maxWidth: 60,
                }}>
                  {[1, 2, 3].map(i => (
                    <Box key={i} sx={{ 
                      height: density === 'comfortable' ? 10 : density === 'compact' ? 5 : 7, 
                      bgcolor: 'divider', 
                      borderRadius: 0.5 
                    }} />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                  {density}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Snippet Length */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Preview Snippet Length
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Number of characters to show in email preview
          </Typography>
          
          <Box sx={{ px: 2 }}>
            <Slider
              value={settings.snippetLength || 100}
              onChange={(_, value) => handleSettingChange('snippetLength', value)}
              min={0}
              max={200}
              step={25}
              marks={[
                { value: 0, label: 'None' },
                { value: 50, label: 'Short' },
                { value: 100, label: 'Medium' },
                { value: 150, label: 'Long' },
                { value: 200, label: 'Full' },
              ]}
              valueLabelDisplay="auto"
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Paper>

        {/* Messages Per Page */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Emails Per Page
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Show</InputLabel>
            <Select
              value={settings.emailsPerPage || 25}
              label="Show"
              onChange={(e) => handleSettingChange('emailsPerPage', Number(e.target.value))}
            >
              {[10, 15, 20, 25, 50, 100].map(n => (
                <MenuItem key={n} value={n}>{n} emails</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Visual Options */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Visual Options
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showSenderAvatar ?? true}
                  onChange={(e) => handleSettingChange('showSenderAvatar', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Show sender avatars</Typography>
                  <Typography variant="caption" color="text.secondary">Display profile pictures in mail list</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.highlightUnread ?? true}
                  onChange={(e) => handleSettingChange('highlightUnread', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Highlight unread emails</Typography>
                  <Typography variant="caption" color="text.secondary">Make unread messages more visible</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.showAttachmentIcon ?? true}
                  onChange={(e) => handleSettingChange('showAttachmentIcon', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Show attachment indicator</Typography>
                  <Typography variant="caption" color="text.secondary">Display icon for emails with attachments</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.showLabelColors ?? true}
                  onChange={(e) => handleSettingChange('showLabelColors', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Show label colors</Typography>
                  <Typography variant="caption" color="text.secondary">Display colored label badges</Typography>
                </Box>
              }
            />
          </Box>
        </Paper>
      </TabPanel>

      {/* Gestures Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Swipe Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure what happens when you swipe an email left or right
          </Typography>

          {/* Swipe Right */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SwipeRight color="success" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Swipe Right</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {swipeActions.map(action => (
                <Box
                  key={action.value}
                  onClick={() => handleSettingChange('swipeRightAction', action.value)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: (settings.swipeRightAction || 'archive') === action.value ? '2px solid' : '1px solid',
                    borderColor: (settings.swipeRightAction || 'archive') === action.value ? 'primary.main' : 'divider',
                    bgcolor: (settings.swipeRightAction || 'archive') === action.value ? 'primary.light' : 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 120,
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  {action.icon && <Box sx={{ color: action.color }}>{action.icon}</Box>}
                  <Typography variant="body2">{action.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Swipe Left */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SwipeLeft color="error" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Swipe Left</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {swipeActions.map(action => (
                <Box
                  key={action.value}
                  onClick={() => handleSettingChange('swipeLeftAction', action.value)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: (settings.swipeLeftAction || 'delete') === action.value ? '2px solid' : '1px solid',
                    borderColor: (settings.swipeLeftAction || 'delete') === action.value ? 'primary.main' : 'divider',
                    bgcolor: (settings.swipeLeftAction || 'delete') === action.value ? 'primary.light' : 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 120,
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  {action.icon && <Box sx={{ color: action.color }}>{action.icon}</Box>}
                  <Typography variant="body2">{action.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Keyboard Shortcuts
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.enableHoverActions ?? true}
                onChange={(e) => handleSettingChange('enableHoverActions', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Show actions on hover</Typography>
                <Typography variant="caption" color="text.secondary">
                  Display quick action buttons when hovering over emails
                </Typography>
              </Box>
            }
          />
        </Paper>
      </TabPanel>

      {/* Content Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            External Images
          </Typography>
          
          <RadioGroup
            value={settings.showImages || 'ask'}
            onChange={(e) => handleSettingChange('showImages', e.target.value)}
          >
            <FormControlLabel
              value="always"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Always show external images</Typography>
                  <Typography variant="caption" color="text.secondary">
                    May affect privacy and load times
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="contacts"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Show for contacts only</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only load images from known senders
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="ask"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Ask before displaying</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Show a prompt to load external images
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="never"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Never show external images</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Best for privacy
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Dynamic Email
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.enableDynamicEmail ?? true}
                onChange={(e) => handleSettingChange('enableDynamicEmail', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Enable dynamic email (AMP)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow interactive content in supported emails
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Link Handling
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showLinkPreviews ?? true}
                  onChange={(e) => handleSettingChange('showLinkPreviews', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Show link previews</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Display preview cards for links in emails
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.openLinksInNewTab ?? true}
                  onChange={(e) => handleSettingChange('openLinksInNewTab', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Open links in new tab</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Links will open in a new browser tab
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.warnExternalLinks ?? true}
                  onChange={(e) => handleSettingChange('warnExternalLinks', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Warn about external links</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Show confirmation before opening external URLs
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default MailViewOptions;
